/**
 * Formats a phone number for display, assuming Indian format.
 * @param phone - The phone number string
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
	if (phone.length >= 10) {
		const cleaned = phone.replace(/\D/g, "");
		return `+${cleaned.slice(0, -10)} ${cleaned.slice(-10, -7)} ${cleaned.slice(-7, -4)} ${cleaned.slice(-4)}`;
	}
	return phone;
};

/**
 * Masks an email address for privacy display.
 * @param email - The email address to mask
 * @returns Masked email address
 */
export const maskEmail = (email: string): string => {
	const [localPart, domain] = email.split("@");
	if (localPart.length <= 2) return email;
	const maskedLocal = localPart.charAt(0) + "*".repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1);
	return `${maskedLocal}@${domain}`;
};

/**
 * Masks a phone number for privacy display.
 * @param phone - The phone number to mask
 * @returns Masked phone number
 */
export const maskPhone = (phone: string): string => {
	if (phone.length < 8) return phone;
	const cleaned = phone.replace(/\D/g, "");
	if (cleaned.length >= 10) {
		return `+${cleaned.slice(0, -10)}***${cleaned.slice(-4)}`;
	}
	return phone;
};

/**
 * Validates an email address using ReDoS-safe manual parsing.
 * This implementation avoids complex regex patterns that could lead to ReDoS attacks.
 * @param email - The email address to validate
 * @returns Whether the email is valid
 */
export const isValidEmail = (email: string): boolean => {
	// Simple length check first
	if (!email || email.length > 320) return false;

	const parts = email.split("@");
	if (parts.length !== 2) return false;

	const [local, domain] = parts;

	// Validate local part (before @)
	if (!local || local.length > 64) return false;
	if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) return false;
	if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) return false;

	// Validate domain part (after @)
	if (!domain || domain.length > 255) return false;

	// Split domain into labels and validate each
	const labels = domain.split(".");
	if (labels.length < 2) return false;

	for (const label of labels) {
		if (!label || label.length > 63) return false;
		if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(label)) return false;
	}

	return true;
};

/**
 * Validates contact information based on verification method.
 * @param verificationMethod - "email" or "phone"
 * @param selectedEmail - Selected email
 * @param selectedPhone - Selected phone
 * @returns Whether the contact is valid
 */
export const isValidContact = (
	verificationMethod: "email" | "phone",
	selectedEmail: string,
	selectedPhone: string,
): boolean => {
	if (verificationMethod === "email") {
		return isValidEmail(selectedEmail);
	} else {
		// Indian phone number validation - must include +91 and be 13 characters total
		const indianPhoneRegex = /^\+91[6-9]\d{9}$/;
		return !!selectedPhone && indianPhoneRegex.test(selectedPhone);
	}
};
